/**
 * PDF 답안지 생성 유틸리티
 *
 * 핵심 원칙:
 * - 1 페이지 = 1 PDF 페이지 (페이지 넘김 금지)
 * - 문제 수에 따라 박스 높이 자동 조절
 * - reference(답작성요령) 필드 표시
 * - 한글 폰트 지원 (NanumGothic)
 */

import jsPDF from 'jspdf';
import type { Problem, AnswerType } from '../types/workbook';
import type { GeneratePdfOptions, ProblemBoxOptions } from '../types/pdf';
import { ANSWER_TYPE_NAMES, ANSWER_TYPE_GUIDES } from '../types/pdf';
import { registerKoreanFont, KOREAN_FONT_NAME } from './koreanFontLoader';

/**
 * 답안지 PDF 생성
 *
 * @param options PDF 생성 옵션
 * @returns Promise<jsPDF> 인스턴스
 *
 * @example
 * ```typescript
 * const pdf = await generateAnswerSheetPdf({
 *   workbookName: '쎈 중등 수학 1-2',
 *   page: 11,
 *   problems: pageProblems,
 *   includeStudentName: true,
 *   includeDate: true,
 * });
 * downloadPdf(pdf, '쎈_중등_수학_1-2_11페이지_답안지.pdf');
 * ```
 */
export async function generateAnswerSheetPdf(options: GeneratePdfOptions): Promise<jsPDF> {
  const {
    workbookName,
    page,
    problems,
    includeStudentName = true,
    includeDate = true,
  } = options;

  // A4 사이즈 PDF 생성
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // 한글 폰트 등록
  await registerKoreanFont(doc);

  // 페이지 크기 상수
  const pageWidth = 210;  // A4 너비 (mm)
  const pageHeight = 297; // A4 높이 (mm)
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  let yPosition = margin;

  // ========== 헤더 ==========
  doc.setFontSize(16);
  doc.setFont(KOREAN_FONT_NAME, 'normal');
  doc.text(workbookName, margin, yPosition);

  doc.setFontSize(12);
  doc.text(`${page}p`, pageWidth - margin - 15, yPosition);

  yPosition += 10;

  // 구분선
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  // ========== 학생 정보 입력란 ==========
  doc.setFontSize(11);
  doc.setFont(KOREAN_FONT_NAME, 'normal');

  if (includeStudentName) {
    doc.text('이름: _____________________', margin, yPosition);
  }
  if (includeDate) {
    doc.text('날짜: _______________', pageWidth - margin - 50, yPosition);
  }

  yPosition += 12;

  // ========== 문제 박스들 ==========
  // ⚠️ 핵심: 페이지 넘김 없이 모든 문제를 한 페이지에 표시

  // 문제 수 정렬
  const sortedProblems = [...problems].sort((a, b) => {
    return a.problem_number - b.problem_number;
  });

  // 참고(reference)가 있는 문제 확인
  const hasReferences = sortedProblems.some(p => p.reference);
  const referenceExtraHeight = hasReferences ? 3 : 0;  // 참고 표시용 추가 높이 (mm)

  // 박스 높이 계산 (모든 문제가 한 페이지에 들어가도록)
  const footerHeight = 15;  // 푸터 공간
  const availableHeight = pageHeight - yPosition - margin - footerHeight;
  const numProblems = sortedProblems.length;
  const minBoxHeight = 12;  // 문제 많을 때 최소 높이
  const maxBoxHeight = 40;  // 문제 적을 때 최대 높이

  // 참고가 있으면 추가 공간 확보
  const effectiveHeight = availableHeight - (referenceExtraHeight * numProblems);
  let boxHeight = effectiveHeight / numProblems;
  boxHeight = Math.max(minBoxHeight, Math.min(maxBoxHeight, boxHeight));

  // ⚠️ 페이지 넘김 없음 - 모든 문제를 현재 페이지에 그림
  for (let i = 0; i < sortedProblems.length; i++) {
    const problem = sortedProblems[i];

    // 문제 박스 그리기 (reference 포함)
    drawProblemBox(doc, {
      x: margin,
      y: yPosition,
      width: contentWidth,
      height: boxHeight - 2,
      problemNumber: problem.problem_number,
      answerType: problem.answer_type as AnswerType,
      reference: problem.reference,
    });

    yPosition += boxHeight;
  }

  // ========== 푸터 (단일 페이지) ==========
  doc.setFontSize(10);
  doc.setTextColor(128);
  doc.text(
    `${workbookName} - ${page}p`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );

  return doc;
}

/**
 * 개별 문제 박스 그리기
 *
 * reference(답작성요령)가 있으면 박스 상단에 연한 색으로 표시
 *
 * @param doc jsPDF 인스턴스
 * @param options 박스 그리기 옵션
 */
function drawProblemBox(doc: jsPDF, options: ProblemBoxOptions): void {
  const { x, y, width, height, problemNumber, answerType, reference } = options;

  // 외곽선
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(x, y, width, height);

  // 문제 번호 영역 (왼쪽)
  const labelWidth = 25;
  doc.line(x + labelWidth, y, x + labelWidth, y + height);

  // 문제 번호
  doc.setFontSize(12);
  doc.setFont(KOREAN_FONT_NAME, 'normal');
  doc.setTextColor(0);
  doc.text(`${problemNumber}`, x + 12, y + height / 2 + 2, { align: 'center' });

  // 답형식
  doc.setFontSize(8);
  doc.setFont(KOREAN_FONT_NAME, 'normal');
  doc.setTextColor(100);
  doc.text(`(${ANSWER_TYPE_NAMES[answerType]})`, x + 12, y + height / 2 + 7, { align: 'center' });

  // ========== 참고(답작성요령) 표시 ==========
  // reference 필드가 있으면 박스 상단에 연한 색으로 표시
  if (reference) {
    doc.setFontSize(8);
    doc.setTextColor(120);  // 연한 회색
    doc.setFont(KOREAN_FONT_NAME, 'normal');
    doc.text(
      `* ${reference}`,
      x + labelWidth + 5,
      y + 5
    );
  }

  // 답 작성 가이드 (연한 색으로, 하단)
  doc.setFontSize(9);
  doc.setTextColor(180);
  doc.text(
    ANSWER_TYPE_GUIDES[answerType],
    x + labelWidth + 5,
    y + height - 3
  );
}

/**
 * PDF 다운로드
 *
 * @param doc jsPDF 인스턴스
 * @param filename 저장할 파일명
 */
export function downloadPdf(doc: jsPDF, filename: string): void {
  doc.save(filename);
}

/**
 * PDF 새 탭에서 열기 (미리보기용)
 *
 * @param doc jsPDF 인스턴스
 */
export function openPdfInNewTab(doc: jsPDF): void {
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
}

/**
 * PDF 직접 프린트
 *
 * @param doc jsPDF 인스턴스
 */
export function printPdf(doc: jsPDF): void {
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);

  const printWindow = window.open(pdfUrl, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
    };
  }
}

/**
 * 여러 페이지의 PDF를 하나로 합쳐서 생성
 *
 * @param workbookName 문제집 이름
 * @param problemsByPage 페이지별 문제 목록
 * @param options 옵션
 * @returns Promise<jsPDF> 인스턴스
 */
export async function generateMultiPagePdf(
  workbookName: string,
  problemsByPage: Map<number, Problem[]>,
  options: { includeStudentName?: boolean; includeDate?: boolean } = {}
): Promise<jsPDF> {
  const { includeStudentName = true, includeDate = true } = options;

  // 첫 번째 페이지로 시작
  const pages = Array.from(problemsByPage.keys()).sort((a, b) => a - b);

  if (pages.length === 0) {
    throw new Error('생성할 페이지가 없습니다.');
  }

  // 첫 페이지 생성 (폰트도 같이 로드됨)
  const firstPage = pages[0];
  const firstProblems = problemsByPage.get(firstPage) || [];
  const doc = await generateAnswerSheetPdf({
    workbookName,
    page: firstPage,
    problems: firstProblems,
    includeStudentName,
    includeDate,
  });

  // 나머지 페이지 추가 (폰트는 이미 로드됨)
  for (let i = 1; i < pages.length; i++) {
    const pageNum = pages[i];
    const pageProblems = problemsByPage.get(pageNum) || [];

    // 새 페이지 추가
    doc.addPage();

    // 해당 페이지 내용을 직접 그리기
    drawPageContent(doc, {
      workbookName,
      page: pageNum,
      problems: pageProblems,
      includeStudentName,
      includeDate,
    });
  }

  return doc;
}

/**
 * 페이지 내용을 현재 문서에 직접 그리기 (내부 함수)
 */
function drawPageContent(
  doc: jsPDF,
  options: GeneratePdfOptions
): void {
  const {
    workbookName,
    page,
    problems,
    includeStudentName = true,
    includeDate = true,
  } = options;

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  let yPosition = margin;

  // 헤더
  doc.setFontSize(16);
  doc.setFont(KOREAN_FONT_NAME, 'normal');
  doc.setTextColor(0);
  doc.text(workbookName, margin, yPosition);

  doc.setFontSize(12);
  doc.text(`${page}p`, pageWidth - margin - 15, yPosition);

  yPosition += 10;

  // 구분선
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  // 학생 정보
  doc.setFontSize(11);
  doc.setFont(KOREAN_FONT_NAME, 'normal');

  if (includeStudentName) {
    doc.text('이름: _____________________', margin, yPosition);
  }
  if (includeDate) {
    doc.text('날짜: _______________', pageWidth - margin - 50, yPosition);
  }

  yPosition += 12;

  // 문제 박스들
  const sortedProblems = [...problems].sort((a, b) => a.problem_number - b.problem_number);
  const hasReferences = sortedProblems.some(p => p.reference);
  const referenceExtraHeight = hasReferences ? 3 : 0;

  const footerHeight = 15;
  const availableHeight = pageHeight - yPosition - margin - footerHeight;
  const numProblems = sortedProblems.length;
  const minBoxHeight = 12;
  const maxBoxHeight = 40;

  const effectiveHeight = availableHeight - (referenceExtraHeight * numProblems);
  let boxHeight = effectiveHeight / numProblems;
  boxHeight = Math.max(minBoxHeight, Math.min(maxBoxHeight, boxHeight));

  for (const problem of sortedProblems) {
    drawProblemBox(doc, {
      x: margin,
      y: yPosition,
      width: contentWidth,
      height: boxHeight - 2,
      problemNumber: problem.problem_number,
      answerType: problem.answer_type as AnswerType,
      reference: problem.reference,
    });

    yPosition += boxHeight;
  }

  // 푸터
  doc.setFontSize(10);
  doc.setTextColor(128);
  doc.text(
    `${workbookName} - ${page}p`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );
}
